import React from "react";
import PropTypes from "prop-types";

import {
  useTable,
  useSortBy,
  useGlobalFilter,
  useFilters,
  useResizeColumns,
  useFlexLayout,
  useExpanded,
  usePagination
} from "react-table";
import { Table, InputGroup, FormControl, Row, Col, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight
} from "@fortawesome/free-solid-svg-icons";
import { matchSorter } from "match-sorter";

import GlobalFilter from "./GlobalFilter";
import { DefaultColumnFilter } from "./Filters";
// import "./DtsReactTable.css";
import "bootstrap/dist/css/bootstrap.min.css";

/** References:
 * ://react-table.tanstack.com/
 * https://blog.logrocket.com/building-styling-tables-react-table-v7/
 *
 * A Good Tutorial Series is also in YouTube:
 * https://www.youtube.com/playlist?list=PLC3y8-rFHvwgWTSrDiwmUsl4ZvipOw9Cz
 */

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
}

// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val;

const DtsReactTable = ({
  columns: userColumns,
  data,
  cypressid,
  renderRowSubComponent,
  rowOnClick = false,
  rowClickHandler,
  headerColor,
  showFooter = false,
  showPagination = false,
  showGlobalFilter,
  subTableId = "", //used for cypress testing
  expandRows = false,
  expandedRowObj = {}
}) => {
  const filterTypes = React.useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,

      includes: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? toString(rowValue).toLowerCase().includes(toString(filterValue).toLowerCase())
            : true;
        });
      },

      startsWith: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            : true;
        });
      }
    }),
    []
  );

  /*const sortTypes = React.useMemo(
    () => ({
      dateSort: (a, b) => {
        a = new Date(a).getTime();
        b = new Date(b).getTime();
        return b > a ? 1 : -1;
      }
    }),
    []
  );*/

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      disableFilters: true,
      minWidth: 30,
      width: 150,
      maxWidth: 500
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { globalFilter, pageIndex, pageSize }
  } = useTable(
    {
      columns: userColumns,
      data,
      initialState: {
        expanded: expandRows ? expandedRowObj : false,
        pageSize: showPagination ? 10 : data.length
      },
      defaultColumn,
      filterTypes
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useResizeColumns,
    useExpanded,
    usePagination,
    useFlexLayout
  );

  return (
    <>
      <Row className='float-right'>
        <Col>{showGlobalFilter ? <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} /> : ""}</Col>
      </Row>
      <Row>
        <Col>
          <Table striped bordered hover size='sm' responsive {...getTableProps()} cypressid={cypressid} aria-label={cypressid ? `${cypressid} data table` : 'Data table'}>
            <thead>
              {headerGroups.map((headerGroup, idx) => {
                const headerGroupProps = headerGroup.getHeaderGroupProps();
                const { key, ...otherProps } = headerGroupProps;
                return (
                  <React.Fragment key={"headerGroup_" + idx}>
                    <tr key={key || ("headerGroup_" + idx)} {...otherProps} className='table-header'>
                      {headerGroup.headers.map((column, colIdx) => {
                        const headerProps = column.getHeaderProps();
                        const { key, ...otherProps } = headerProps;
                        return (
                          <th
                            key={key || ("headerCol_" + colIdx)}
                            scope="col"
                            aria-sort={column.isSorted ? (column.isSortedDesc ? 'descending' : 'ascending') : (column.canSort ? 'none' : undefined)}
                            className={`p-2 table-header ${headerColor ? "primary-" + headerColor : "primary-deq"}`}
                            {...otherProps}
                          >
                            {/* If header is empty, add visually hidden text for <th> as well, outside the button */}
                            {(() => {
                              const header = column.render("Header");
                              if (!header || (typeof header === 'string' && header.trim() === '')) {
                                return <span className="visually-hidden">{column.id ? column.id : 'Column'}</span>;
                              }
                              return null;
                            })()}
                            <button
                              {...column.getSortByToggleProps({ title: undefined })}
                              disabled={!column.canSort}
                              aria-label={(() => {
                                const header = column.render("Header");
                                if (!header || (typeof header === 'string' && header.trim() === '')) {
                                  return column.id ? column.id : 'Column';
                                }
                                return column.canSort ? (column.isSorted ? (column.isSortedDesc ? `${header}, sorted descending, activate to sort ascending` : `${header}, sorted ascending, activate to sort descending`) : `${header}, activate to sort`) : undefined;
                              })()}
                              style={{ background: 'none', border: 'none', cursor: column.canSort ? 'pointer' : 'default', padding: 0 }}
                            >
                              {(() => {
                                const header = column.render("Header");
                                if (!header || (typeof header === 'string' && header.trim() === '')) {
                                  return null;
                                }
                                return <>{header}</>;
                              })()}
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <FontAwesomeIcon className='ms-3' icon={faArrowDown} aria-hidden="true" />
                                ) : (
                                  <FontAwesomeIcon className='ms-3' icon={faArrowUp} aria-hidden="true" />
                                )
                              ) : (
                                ""
                              )}
                            </button>
                            <div {...column.getResizerProps()} className='resizer' />
                            {column.canResize && (
                              <div
                                {...column.getResizerProps()}
                                className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                              />
                            )}
                            <div>{column.canFilter ? column.render("Filter") : null}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                const rowProps = row.getRowProps(rowOnClick ? { style: { cursor: 'pointer' } } : {});
                const { key, ...otherProps } = rowProps;
                return (
                  <React.Fragment key={key}>
                    <tr key={key}
                      {...otherProps}
                      onClick={rowOnClick ? () => rowClickHandler?.(row.original) : undefined}
                      onKeyDown={rowOnClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); rowClickHandler?.(row.original); } } : undefined}
                      tabIndex={rowOnClick ? 0 : undefined}
                    >
                      {row.cells.map((cell, cellIdx) => {
                        return <td {...cell.getCellProps()} key={"cell_" + cellIdx}>{cell.render("Cell")}</td>;
                      })}
                    </tr>
                    {row.isExpanded ? (
                      <tr>
                        <td data-cypressid={row.index + subTableId}>
                          <span className='subTable'>{renderRowSubComponent({ row })}</span>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
            {showFooter ? (
              <tfoot className='fw-bold'>
                {footerGroups.map((group, groupIdx) => {
                  const groupProps = group.getFooterGroupProps();
                  const { key, ...otherProps } = groupProps;
                  return (
                    <tr key={key || ("footerGroup_" + groupIdx)} {...otherProps}>
                      {group.headers.map((column, colIdx) => (
                        <td {...column.getFooterProps()} key={"footerCol_" + colIdx}>{column.render("Footer")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tfoot>
            ) : (
              ""
            )}
          </Table>
          {showPagination ? (
            <Row className='mt-2 text-center'>
              <Col>
                <Button
                  className='me-2 pagination-btn'
                  variant='secondary'
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                  aria-label='First page'
                >
                  <FontAwesomeIcon icon={faAngleDoubleLeft} aria-hidden='true' />
                </Button>
                <Button
                  className='me-2 pagination-btn'
                  variant='secondary'
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  aria-label='Previous page'
                >
                  <FontAwesomeIcon icon={faAngleLeft} aria-hidden='true' />
                </Button>
              </Col>
              <Col>
                <span>
                  Page
                  <strong>
                    {pageIndex + 1} of {pageOptions.length}
                  </strong>
                </span>
                <span>
                  <label htmlFor="gotoPageInput" className="me-1">| Go to page:</label>
                  <InputGroup size='sm' style={{ width: "20%", display: "inline-flex" }}>
                    <FormControl
                      id="gotoPageInput"
                      type='number'
                      min={1}
                      max={pageOptions.length}
                      defaultValue={pageIndex + 1}
                      onChange={(e) => {
                        const page = e.target.value ? Number(e.target.value) - 1 : 0;
                        gotoPage(page);
                      }}
                    />
                  </InputGroup>
                </span>
                <InputGroup size='sm' style={{ width: "30%", display: "inline-flex" }}>
                  <FormControl
                    className='mt-4'
                    size='sm'
                    as='select'
                    aria-label='Rows per page'
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </FormControl>
                </InputGroup>
              </Col>
              <Col>
                <Button
                  className='me-2 pagination-btn'
                  variant='secondary'
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  aria-label='Next page'
                >
                  <FontAwesomeIcon icon={faAngleRight} aria-hidden='true' />
                </Button>
                <Button
                  className='me-2 pagination-btn'
                  variant='secondary'
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                  aria-label='Last page'
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} aria-hidden='true' />
                </Button>
              </Col>
            </Row>
          ) : (
            ""
          )}
        </Col>
      </Row>
    </>
  );
};

DtsReactTable.propTypes = {
  /**Show Footer */
  showFooter: PropTypes.bool,

  /** Specified if pagination should show or not */
  showPagination: PropTypes.bool,

  /** Specifies if there should be a row onClick action*/
  rowOnClick: PropTypes.bool,

  /** OPTIONAL: The onClick Action to be taken */
  rowClickHandler: PropTypes.func,

  /** header color background. There are six possible choices. Refer to ReadMe file for specifics */
  headerColor: PropTypes.string,

  subTableId: PropTypes.string
};

export default DtsReactTable;
